/* eslint-disable @typescript-eslint/no-use-before-define */

import compact from 'lodash/compact';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import flatten from 'lodash/flatten';

import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {ListOperator, LogicalOperator} from 'common/widgets/property-filter-menu/models/types';
import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {PropertyType, ResourceType} from 'common/report/constants';

import {CohortSegment, isInlineCohortSegment, SavedCohortSegment} from '../segment-menu/types';
import * as PropertiesMenu from '../properties-menu/types';
import {BookmarkCohortFilter, CohortFilterTree, CohortFilterTreeChild} from './types';

function generateCohortFilterTree(filter: PropertyFilter) {
  const cohorts = filter.filterValue as CohortSegment[];
  const negated = filter.filterOperator === ListOperator.DoesNotContain;

  return generateCohortFilterSubTree(cohorts, negated);
}

function generateCohortFilterSubTree(cohorts: CohortSegment[], negated: boolean): CohortFilterTree {
  if (!cohorts.length) {
    return null;
  }

  const cohortFilter = getFilterByCohort(cohorts[0], negated);
  const remainingCohorts = cohorts.slice(1);

  // eslint-disable-next-line sonarjs/prefer-immediate-return
  const filterByCohort = {
    operator: LogicalOperator.Or,
    children: compact([
      cohortFilter,
      remainingCohorts.length === 1
        ? getFilterByCohort(remainingCohorts[0], negated)
        : generateCohortFilterSubTree(remainingCohorts, negated),
    ]),
  };

  // @ts-ignore - TODO: Type '{ operator: LogicalOperator; children: (CohortFilterTree | { cohort: { id: number; name: string; negated: boolean; }; })[]; }' is not assignable to type 'CohortFilterTree'.
  return filterByCohort;
}

export function getFilterByCohorts(filters: PropertyFilter[]): CohortFilterTree {
  if (filters.length === 1) {
    return generateCohortFilterTree(filters[0]);
  }

  return {
    operator: LogicalOperator.And,
    children: [generateCohortFilterTree(filters[0]), getFilterByCohorts(filters.slice(1))],
  };
}

function getFilterByCohort(filter: CohortSegment, negated = false) {
  if ((filter as SavedCohortSegment).cohort.id) {
    const {cohort} = filter as SavedCohortSegment;
    return {
      cohort: {
        id: cohort.id,
        name: cohort.name,
        negated,
        ...(cohort.data_group_id ? {data_group_id: cohort.data_group_id} : {}),
      },
    };
  }

  // Inline cohort
  return {
    cohort: {
      ...filter.cohort,
      negated,
    },
  };
}

function isBookmarkCohortFilter(filter: BookmarkCohortFilter | CohortFilterTree): filter is BookmarkCohortFilter {
  return filter && Object.prototype.hasOwnProperty.call(filter, `cohort_id`);
}

function isBookmarkCohortTree(filter: CohortFilterTreeChild): filter is CohortFilterTree {
  return filter && Object.prototype.hasOwnProperty.call(filter, `operator`);
}

/**
 * Parses filter by cohorts
 */
// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function bookmarkFilterByCohortToCohortFilters(
  filterByCohort: string | CohortFilterTree | BookmarkCohortFilter | UnknownObj,
): PropertyFilter[] {
  const filters = [];
  const cohortFilter: CohortFilterTree | BookmarkCohortFilter = isString(filterByCohort)
    ? JSON.parse(filterByCohort || null)
    : filterByCohort;
  const getCohortFilter = (cohort, negated = null): CohortSegment => {
    if (cohort.raw_cohort) {
      return {
        cohort: {
          raw_cohort: cohort.raw_cohort,
          name: cohort.name,
          negated: negated !== null ? negated : cohort.negated,
        },
      };
    }

    return {
      cohort: {
        id: cohort.id || cohort.cohort_id,
        name: cohort.name,
        negated: negated !== null ? negated : cohort.negated,
        ...(cohort.data_group_id ? {data_group_id: cohort.data_group_id} : {}),
      },
    };
  };

  if (isEmpty(cohortFilter)) {
    return filters;
  } else if (isBookmarkCohortFilter(cohortFilter)) {
    // handle single cohort case
    return [
      new PropertyFilter({
        filterOperator: cohortFilter.negated ? ListOperator.DoesNotContain : ListOperator.Contains,
        filterValue: [getCohortFilter(cohortFilter)],
        propertyType: PropertyType.List,
        resourceType: ResourceType.Cohort,
      }),
    ];
  }
  return collectFilterGroups(cohortFilter, true) as PropertyFilter[];

  /**
   * Traverse the cohort tree to produce distinct groups of cohort filters.
   *
   * Assumption: the high level filter groups are joined by ANDs at the top,
   * and ANDs always come above ORs in the cohort filter tree.
   */
  function collectFilterGroups(node: CohortFilterTree | CohortFilterTreeChild, isRoot = false) {
    if (isBookmarkCohortTree(node)) {
      const {operator} = node;
      let filterValues;
      if (operator === LogicalOperator.And) {
        // AND nodes produce distinct groups of CohortSegments (array of arrays)
        // concat the children as they come back
        filterValues = node.children
          .map(function mapChildrenOfAndNode(child) {
            return collectFilterGroups(child);
          })
          .reduce((arr, child) => arr.concat(child), []);
      } else if (operator === LogicalOperator.Or) {
        // OR nodes produce one group of CohortSegments (single array)
        // wrap in array on the way up so we know it's a distinct group:  [[v1, v2]]
        filterValues = [
          node.children.flatMap(function mapChildrenOfOrNode(child) {
            // response will come back wrapped in array, so unwrap with flatten
            return flatten(collectFilterGroups(child));
          }),
        ];
      } else {
        throw new Error(`Invalid cohort operator: ${operator}`);
      }

      if (isRoot) {
        return filterValues.map(toPropertyFilter);
      } else {
        return filterValues;
      }
    } else {
      // this is a leaf node, return it wrapped so it may be combined with other leaves
      return [getCohortFilter(node.cohort)];
    }
  }

  function toPropertyFilter(maybeCohortList: CohortSegment | CohortSegment[]) {
    let cohortList: CohortSegment[];
    if (!Array.isArray(maybeCohortList)) {
      cohortList = [maybeCohortList];
    } else {
      cohortList = maybeCohortList;
    }
    const shouldNegate = cohortList[0].cohort.negated;
    return new PropertyFilter({
      filterOperator: shouldNegate ? ListOperator.DoesNotContain : ListOperator.Contains,
      filterValue: cohortList.map((cohort) => {
        if (shouldNegate) {
          cohort.cohort.negated = false;
        }
        return cohort;
      }),
      propertyType: PropertyType.List,
      resourceType: ResourceType.Cohort,
    });
  }
}

/**
 * Generates tracking props for a given PropertyFilter
 */
export function generateFilterTrackingProps(item: PropertyFilter, extraProps?: Obj) {
  const isCohort = item.resourceType === ResourceType.Cohort;
  const cohortNames =
    isCohort &&
    Array.isArray(item.filterValue) &&
    (item.filterValue as CohortSegment[]).map(
      (filter) => filter.cohort.name ?? (filter as SavedCohortSegment)?.cohort?.id,
    );

  return {
    'Property name': item.propertyName ?? null,
    'Property data type': item.propertyType,
    'Property default type': item.propertyDefaultType ?? null,
    'Property join type': item.joinPropertyType ?? null,
    'Property list quantifier': item.listQuantifier ?? null,
    'Resource type': item.resourceType,
    'Filter operator': item.filterOperator,
    'Filter value': isCohort ? cohortNames : item.filterValue,
    [commonTrackingEventPropertyName.usedLookupTableProperties]: !!item.dataGroupId && isArray(item.propertyName),
    [commonTrackingEventPropertyName.usedEventCustomProperty]:
      Boolean(item.customPropertyId || item.customProperty) && item.resourceType === ResourceType.Event,
    [commonTrackingEventPropertyName.usedUserCustomProperty]:
      Boolean(item.customPropertyId || item.customProperty) && item.resourceType === ResourceType.User,
    [commonTrackingEventPropertyName.dataGroupId]: item.dataGroupId ?? null,
    ...extraProps,
  };
}

/**
 * Obtains a property item from a selected property filter
 */
export function transformFilterToPropertyItem(selection: Partial<PropertyFilter>): PropertiesMenu.Selection {
  const selectionResourceType = selection && selection.resourceType;

  switch (selectionResourceType) {
    case ResourceType.Cohort: {
      const filterValue = selection.filterValue as CohortSegment[];
      return filterValue?.map((entry) => {
        if (isInlineCohortSegment(entry)) {
          return {
            resourceType: ResourceType.Cohort,
            label: entry.cohort.name,
            value: entry,
          };
        } else {
          return {
            resourceType: ResourceType.Cohort,
            label: entry.cohort.name,
            value: entry.cohort.id,
          };
        }
      });
    }
    case ResourceType.Other:
    case ResourceType.User:
    case ResourceType.Event: {
      const propertyType =
        selection.propertyType || (selection.customProperty && selection.customProperty.propertyType);
      return {
        ...(selection.customPropertyId ? {customPropertyId: selection.customPropertyId} : null),
        ...(selection.customProperty ? {customProperty: selection.customProperty} : null),
        ...(selection.joinPropertyType ? {joinPropertyType: selection.joinPropertyType} : null),
        ...(selection.tempDataGroupId ? {tempDataGroupId: selection.tempDataGroupId} : null),
        ...(selection.behavior ? {behavior: selection.behavior} : null),
        dataGroupId: selection.dataGroupId,
        propertyDefaultType: selection.propertyDefaultType || propertyType,
        ...(selection.propertyObjectKey
          ? {
              propertyObjectKey: selection.propertyObjectKey,
            }
          : {}),
        resourceType: selectionResourceType,
        type: propertyType,
        value: selection.propertyName,
      };
    }
    default:
      return null;
  }
}
